import { Component, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import VehicleAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/transport/vehicle/vehicle.entity';
import { VehicleSelectorModalService } from './vehicle-selector-modal.service';
import {
  SmartGridComponent,
  ColDef,
  GridConfig,
  SelectionChangedEvent,
  PageChangedEvent
} from 'app/shared/components/smart-grid';
import { VehicleNavCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/vehicle-nav-cell-renderer/vehicle-nav-cell-renderer.component';
import { CompanyNavCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/company-nav-cell-renderer/company-nav-cell-renderer.component';
import { BusinessPartnerNavCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/business-partner-nav-cell-renderer/business-partner-nav-cell-renderer.component';

/** Datos de entrada para el modal */
export interface VehicleSelectorModalData {
  selectedVehicles?: VehicleAttributes[];
  title?: string;
}

/** Resultado devuelto al cerrar el modal */
export interface VehicleSelectorModalResult {
  vehicles: VehicleAttributes[];
}

@Component({
  selector: 'app-vehicle-selector-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    SmartGridComponent
  ],
  providers: [VehicleSelectorModalService],
  templateUrl: './vehicle-selector-modal.component.html',
  styleUrl: './vehicle-selector-modal.component.scss'
})
export class VehicleSelectorModalComponent {
  @ViewChild(SmartGridComponent) grid!: SmartGridComponent<VehicleAttributes>;

  private readonly dialogRef = inject(MatDialogRef<VehicleSelectorModalComponent>);
  private readonly data = inject<VehicleSelectorModalData>(MAT_DIALOG_DATA, { optional: true });
  protected readonly service = inject(VehicleSelectorModalService);
  protected readonly searchControl = new FormControl('');

  readonly title: string;

  /** Toggle para mostrar/ocultar preview de seleccionados */
  showSelectedPreview = false;

  /** Definición de columnas para SmartGrid */
  readonly columnDefs: ColDef<VehicleAttributes>[] = [
    {
      field: 'name',
      headerName: 'Nombre',
      width: 200,
      cellRenderer: VehicleNavCellRendererComponent
    },
    {
      field: 'type.name' as any,
      headerName: 'Tipo',
      width: 120,
      valueFormatter: (value) => value || '-'
    },
    {
      field: 'plate',
      headerName: 'Placa',
      width: 100,
      valueFormatter: (value) => value || '-'
    },
    {
      field: 'company.name' as any,
      headerName: 'Compañía',
      width: 150,
      cellRenderer: CompanyNavCellRendererComponent,
      cellRendererParams: { dataPath: 'company' }
    },
    {
      field: 'businessPartner.name' as any,
      headerName: 'Propietario',
      width: 150,
      cellRenderer: BusinessPartnerNavCellRendererComponent,
      cellRendererParams: { dataPath: 'businessPartner' }
    }
  ];

  /** Configuración del SmartGrid */
  readonly gridConfig: GridConfig<VehicleAttributes> = {
    rowIdField: '_id',
    selection: {
      enabled: true,
      mode: 'multiple',
      showCheckboxColumn: true,
      selectOnRowClick: false,
      rememberSelection: true
    },
    pagination: {
      enabled: true,
      mode: 'api',
      pageSize: 10,
      pageSizeOptions: [10, 25, 50]
    },
    sorting: {
      enabled: false
    },
    search: {
      enabled: false
    },
    columnFilters: {
      enabled: false
    },
    styling: {
      maxHeight: '400px',
      stripedRows: true,
      density: 'comfortable'
    },
    emptyState: {
      message: 'No hay vehículos disponibles',
      showIcon: true
    }
  };

  constructor() {
    this.title = this.data?.title ?? 'Seleccionar Vehículos';

    // Inicializar selección con vehículos pre-seleccionados
    if (this.data?.selectedVehicles?.length) {
      this.service.initializeSelection(this.data.selectedVehicles);
    }

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => this.service.setSearch$.next(value ?? ''));
  }

  onConfirm(): void {
    const result: VehicleSelectorModalResult = {
      vehicles: this.service.selectedVehicles()
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  onPageChange(event: PageChangedEvent): void {
    this.service.setPage$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  onSelectionChanged(event: SelectionChangedEvent<VehicleAttributes>): void {
    this.service.setSelectedVehicles(event.selectedRows);
  }

  onToggleVehicle(vehicle: VehicleAttributes): void {
    this.service.toggleVehicle$.next(vehicle);
  }
}
